import { useState, useEffect, useCallback } from "react";
import DraggableDualList from "../DraggableDualList/DraggableDualList";
import { useUserStore } from "../../contexts/userStore";
import AccessModuleService from "../../services/AccessModuleService";
import { listRfSlots } from "../../helpers/AccessPointParser";
import { AccessIcons } from "../../utils/accessIcons";

// ─── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Build a unique item id matching the old component format.
 */
function makeItemId(accessPoint) {
   return `${accessPoint.id}${accessPoint.subModuleIndex != null ? `/${accessPoint.subModuleIndex}` : ""}`;
}

/**
 * Convert a flat list of access points into a tree of groups (by condominium),
 * compatible with DraggableDualList's node format.
 *
 * @param {Array} accessPoints         – flat items from listRfSlots()
 * @param {Array} condominiums         – from userStore
 * @param {Array} invitationAccessModules – invitation.invitationAccessModules (or []) for filtering
 * @param {boolean} includeSelected    – if true, only include points that belong to the invitation;
 *                                       if false, only include points that do NOT belong.
 */
function buildTree(accessPoints, condominiums, invitationAccessModules, includeSelected) {
   const groupMap = new Map(); // condominiumId → group node

   for (const ap of accessPoints) {
      const belongsToInvitation = invitationAccessModules.find(
         (ia) =>
            ia.moduleId === ap.id &&
            (ap.subModuleIndex === undefined ||
               (ap.subModuleIndex !== null &&
                  Number(ia.accessIndex) === ap.subModuleIndex))
      );

      const shouldInclude = includeSelected ? !!belongsToInvitation : !belongsToInvitation;
      if (!shouldInclude) continue;

      const condoId = ap.id_condominium;
      const condo = condominiums?.find((c) => Number(c.id) === condoId);

      if (!condo) continue;

      const condoName = condo?.name ?? `Condomínio ${condoId}`;
      const groupId = `condo-${condoId}`;

      if (!groupMap.has(groupId)) {
         groupMap.set(groupId, {
            id: groupId,
            type: "group",
            label: condoName,
            children: [],
         });
      }

      const itemId = makeItemId(ap);
      groupMap.get(groupId).children.push({
         id: itemId,
         type: "item",
         label: ap.name,
         icon: AccessIcons[ap.type] ?? null,
         // Keep original references for onChange reconstruction
         originalId: ap.id,
         subModuleIndex: ap.subModuleIndex,
         relationId: belongsToInvitation?.id,
      });
   }

   return Array.from(groupMap.values()).filter((g) => g.children.length > 0);
}

/**
 * Flatten a tree's leaf items into the same format expected by onChangeList.
 */
function flattenSelectedItems(rightTree) {
   const selected = [];
   for (const node of rightTree) {
      const items = node.type === "group" ? node.children : [node];
      for (const item of items) {
         if (item.type === "item") {
            selected.push({
               id: item.id,
               relationId: item.relationId,
               originalId: item.originalId,
               subIndex: item.subModuleIndex,
               value: item.id,
               label: item.label,
               icon: item.icon,
            });
         }
      }
   }
   return selected;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function InvitationAccessModulesDDL({ invitation, onChangeList }) {
   const { clientLicenses, condominiums } = useUserStore();
   const [condominiumAccessPoints, setCondominiumAccessPoints] = useState([]);
   const [initialLeft, setInitialLeft] = useState([]);
   const [initialRight, setInitialRight] = useState([]);
   const [ready, setReady] = useState(false);

   // Fetch all access points for the client's licenses
   useEffect(() => {
      if (!clientLicenses?.length) return;
      (async () => {
         const { data: response } = await AccessModuleService.listByLicences(
            clientLicenses.map((l) => l.id)
         );
         setCondominiumAccessPoints(listRfSlots(response));
      })();
   }, [clientLicenses]);

   // Once access points are loaded, split them into left / right trees
   useEffect(() => {
      if (!condominiumAccessPoints.length) return;
      const invitationAccessModules = invitation?.invitationAccessModules ?? [];

      const left = buildTree(condominiumAccessPoints, condominiums, invitationAccessModules, false);
      const right = buildTree(condominiumAccessPoints, condominiums, invitationAccessModules, true);

      setInitialLeft(left);
      setInitialRight(right);
      setReady(true);

      // Notify parent with the initially selected items
      if (onChangeList) onChangeList(flattenSelectedItems(right));
   }, [condominiumAccessPoints]);

   // Called by DraggableDualList whenever the user moves items
   const handleChange = useCallback(
      (_leftTree, rightTree) => {
         if (onChangeList) onChangeList(flattenSelectedItems(rightTree));
      },
      [onChangeList]
   );

   if (!ready) return null; // or a loading skeleton

   return (
      <DraggableDualList
         labelLeft="Acessos Disponíveis"
         labelRight="Acessos Selecionados"
         initialLeft={initialLeft}
         initialRight={initialRight}
         onChange={handleChange}
      />
   );
}
